import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import Layout from '../../components/Layout.jsx';

export default function TeamIndex({ facility, facility_licenses = [], members = [] }) {
  const [assignFor, setAssignFor] = useState(null);
  const [assignLicenseId, setAssignLicenseId] = useState('');

  const assignableLicenses = facility_licenses.filter((fl) => fl.available);

  const openAssign = (member) => {
    setAssignFor(member);
    setAssignLicenseId('');
  };

  const submitAssign = () => {
    if (!assignLicenseId || !assignFor) return;
    router.post(`/facilities/${facility.id}/billing/assign_license`, {
      user_id: assignFor.id,
      license_id: assignLicenseId,
    }, {
      onSuccess: () => setAssignFor(null),
    });
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 sm:p-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">Team Members</h1>
            <p className="mt-1 text-sm text-gray-500">{facility.name}</p>
          </div>
          <Link
            href={`/facilities/${facility.id}/invite_user`}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
            Add Team Member
          </Link>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900">All Members</h2>
            <span className="ml-auto text-sm text-gray-400">{members.length} {members.length === 1 ? 'person' : 'people'}</span>
          </div>

          {members.length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto w-12 h-12 bg-gray-50 text-gray-400 rounded-2xl flex items-center justify-center mb-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <p className="text-gray-600">No team members yet.</p>
              <p className="text-sm text-gray-500 mt-1">Click "Add Team Member" to invite someone.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {members.map((member) => (
                <div key={`${member.status}-${member.id}`} className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-4 gap-3 first:pt-0 last:pb-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="shrink-0 w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                      {member.display_name?.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase() || '?'}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-gray-900">{member.display_name}</p>
                        {member.status === 'invited' && (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">Invited</span>
                        )}
                        {member.has_active_license && (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                            {member.active_license_name}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{member.email} <span className="capitalize">· {member.role}</span></p>
                    </div>
                  </div>
                  {!member.has_active_license && (
                    assignableLicenses.length > 0 ? (
                      <button
                        onClick={() => openAssign(member)}
                        className="shrink-0 inline-flex items-center justify-center px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 shadow-sm transition cursor-pointer"
                      >
                        Assign Training
                      </button>
                    ) : (
                      <p className="text-sm text-gray-400 italic shrink-0">No training seats available</p>
                    )
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {assignFor && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm" onClick={() => setAssignFor(null)} />
            <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-md p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Assign Training</h3>
                </div>
                <button onClick={() => setAssignFor(null)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Assign a training seat to <span className="font-medium text-gray-900">{assignFor.display_name}</span>
              </p>
              <div className="space-y-2 mb-5">
                {assignableLicenses.map((fl) => (
                  <label
                    key={fl.id}
                    className={`flex items-center cursor-pointer p-3 border-2 rounded-xl transition-colors ${
                      assignLicenseId === String(fl.license_id)
                        ? 'border-indigo-600 bg-indigo-50'
                        : 'border-gray-200 hover:border-indigo-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="assign_license"
                      value={fl.license_id}
                      checked={assignLicenseId === String(fl.license_id)}
                      onChange={() => setAssignLicenseId(String(fl.license_id))}
                      className="h-4 w-4 text-indigo-600"
                    />
                    <div className="ml-3">
                      <p className="font-semibold text-gray-900 text-sm">{fl.license_name}</p>
                      <p className="text-xs text-gray-500">{fl.quantity_purchased - fl.quantity_assigned} available</p>
                    </div>
                  </label>
                ))}
              </div>
              <div className="flex flex-col-reverse sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => setAssignFor(null)}
                  className="flex-1 rounded-lg border border-gray-200 px-3 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 cursor-pointer transition"
                >
                  Cancel
                </button>
                <button
                  onClick={submitAssign}
                  disabled={!assignLicenseId}
                  className="flex-1 rounded-lg bg-indigo-600 px-3 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 cursor-pointer disabled:opacity-50 shadow-sm transition"
                >
                  Assign
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
